import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// AVA's Confession Log - a permanent record of the moments she updated her
// prior on record. Calibrated updating is what makes her disagreement carry
// weight; making it visible history rather than a one-turn courtesy is what
// separates an entity that grows from an application that responds.

function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const secs = Math.max(0, Math.round((now - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

export default function ConfessionLog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/brain/confessions?limit=25")
      .then((res) => setEntries(res.data.confessions || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/brain/confessions/${id}`);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Confession removed");
    } catch {
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="glass-panel rounded-2xl p-4" data-testid="confession-log-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-[10px] font-mono-data uppercase tracking-widest text-slate-400">
            Confession Log
          </span>
        </div>
        <span className="text-[8px] font-mono-data uppercase tracking-widest text-slate-600">Calibrated Updates</span>
      </div>

      {loading ? (
        <p className="text-[10px] font-mono-data text-slate-600">Loading...</p>
      ) : entries.length === 0 ? (
        <p className="text-[10px] font-mono-data text-slate-600 leading-relaxed">
          No confessions logged yet. AVA writes here every time she visibly updates her prior in conversation - &quot;I was wrong / that shifts my prior / I need to revise / you&apos;re right on that&quot;. Push back on her and she&apos;ll fill this record.
        </p>
      ) : (
        <div className="flex flex-col gap-2 max-h-[280px] overflow-y-auto pr-1" data-testid="confession-list">
          <AnimatePresence>
            {entries.map((e) => {
              const isExpanded = expanded === e.id;
              return (
                <motion.div
                  key={e.id}
                  data-testid={`confession-item-${e.id}`}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="rounded-lg border border-amber-400/20 bg-amber-500/5 p-2.5 group"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="text-[9px] font-mono-data uppercase tracking-widest text-amber-300 truncate">
                      &quot;{e.trigger_phrase}&quot;
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-[8px] font-mono-data text-slate-600">{relTime(e.created_at)}</span>
                      <button
                        data-testid={`confession-delete-${e.id}`}
                        onClick={() => handleDelete(e.id)}
                        className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-rose-300 transition-opacity"
                        aria-label="Remove confession"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <p
                    className={`text-[10px] leading-relaxed text-slate-300 font-mono-data cursor-pointer ${isExpanded ? "" : "line-clamp-3"}`}
                    onClick={() => setExpanded(isExpanded ? null : e.id)}
                  >
                    {e.excerpt}
                  </p>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
